import { useState, useMemo } from "react";
import moment from "moment-timezone";
import { useSearchParams } from "react-router-dom";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { 
  CertificateInstance, 
  useListCertificateInstances, 
  useUpdateCertificateInstance,
  useDeleteCertificateInstance,
  CertificateInstanceStatusEnum,
  type CertificateInstanceStatus,
} from "@/lib/api/birthdayFundCertificates";
import { GenerateLoadingOrError, CommonDestinationTimezone } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import { ViewBirthdayFundRequest } from "./BirthdayFundModal";

const formatStatus = (status: string): string => {
  if (!status) return "";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getExpirationStyle = (expirationDate: string): { text: string; className: string } => {
  if (!expirationDate) return { text: "", className: "" };
  
  const now = moment();
  const expDate = moment(expirationDate);
  const daysUntilExpiration = expDate.diff(now, "days");
  
  if (daysUntilExpiration < 0) {
    // Expired - red
    return { 
      text: expDate.format("MM/DD/YYYY"), 
      className: "text-red-600 font-semibold" 
    };
  } else if (daysUntilExpiration <= 10) {
    // Expiring soon - yellow
    return { 
      text: expDate.format("MM/DD/YYYY"), 
      className: "text-yellow-600 font-semibold" 
    };
  }
  
  return { 
    text: expDate.format("MM/DD/YYYY"), 
    className: "text-gray-600 text-sm" 
  };
};

const statusOptions = [
  { value: "sent", label: "Sent" },
  { value: "invoiced", label: "Invoiced" },
  { value: "paid", label: "Paid" },
];

type ExpirationFilterType = "expired" | "not_expired" | "about_to_expire";

const getExpirationStatus = (
  expirationDate: string | null | undefined
): ExpirationFilterType | null => {
  if (!expirationDate) return null;

  const now = moment();
  const expDate = moment(expirationDate);

  if (expDate.isBefore(now, "day")) {
    return "expired";
  } else if (expDate.isSameOrBefore(now.clone().add(10, "days"), "day")) {
    return "about_to_expire";
  }

  return "not_expired";
};

type FilterState = Record<ExpirationFilterType, boolean>;

export function BirthdayFundCertificateList() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const certificateInstancesResult = useListCertificateInstances();
  const updateCertificateInstance = useUpdateCertificateInstance();
  const deleteCertificateInstance = useDeleteCertificateInstance();
  
  const [selectedCertificate, setSelectedCertificate] = useState<CertificateInstance | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState<CertificateInstance | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Preset filter state - all on by default
  const [filters, setFilters] = useState<FilterState>({
    expired: true,
    not_expired: true,
    about_to_expire: true,
  });

  // Filter instances based on preset filters - must be called before early return
  const filteredInstances = useMemo(() => {
    const instances = certificateInstancesResult?.data?.data?.instances || [];
    return instances.filter((instance: CertificateInstance) => {
      const status = getExpirationStatus(instance.expiration);
      if (!status) return true;
      return filters[status];
    });
  }, [certificateInstancesResult, filters]);

  const loadingOrErrorUi = GenerateLoadingOrError([certificateInstancesResult]);
  if (loadingOrErrorUi) return loadingOrErrorUi;

  const toggleFilter = (filter: ExpirationFilterType) => {
    setFilters((prev) => ({
      ...prev,
      [filter]: !prev[filter],
    }));
  };

  const handleRowClick = (row: CertificateInstance) => {
    setSelectedCertificate(row);
    setSelectedStatus(row.status || "");
    setIsModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedCertificate || !selectedStatus) return;
    
    setIsUpdating(true);
    try {
      const result = await updateCertificateInstance(selectedCertificate.id, {
        status: selectedStatus as CertificateInstanceStatus,
      });
      
      if (result.success) {
        toast({
          title: "Status Updated",
          description: `Certificate status updated to ${formatStatus(selectedStatus)}`,
        });
        setIsModalOpen(false);
      } else {
        throw new Error(result.message || "Failed to update status");
      }
    } catch (err: any) {
      toast({
        title: "Update Failed",
        description: err.message || "Failed to update certificate status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (row: CertificateInstance) => {
    setCertificateToDelete(row);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!certificateToDelete) return;
    
    setIsDeleting(true);
    try {
      const result = await deleteCertificateInstance(certificateToDelete.id);
      
      if (result.success) {
        toast({
          title: "Certificate Deleted",
          description: "The certificate has been successfully deleted",
        });
        setIsDeleteModalOpen(false);
        setCertificateToDelete(null);
      } else {
        throw new Error(result.message || "Failed to delete certificate");
      }
    } catch (err: any) {
      toast({
        title: "Delete Failed",
        description: err.message || "Failed to delete certificate",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: DataTableColumn<CertificateInstance>[] = [
    {
      id: "certificate_id",
      accessorKey: "certificate_id",
      accessorFn: (row: CertificateInstance) => row.certificate_id,
      header: "Certificate ID",
      cell: (info) => info.getValue() as string,
    },
    {
      id: "location_name",
      accessorKey: "location_name",
      accessorFn: (row: CertificateInstance) => row.location_name,
      header: "Location Name",
      cell: (info) => info.getValue() as string,
    },
    {
      id: "status",
      accessorKey: "status",
      accessorFn: (row: CertificateInstance) => row.status,
      header: "Status",
      cell: (info) => (
        <span className="text-sm font-medium">
          {formatStatus(info.getValue() as string)}
        </span>
      ),
    },
    {
      id: "expiration_date",
      accessorKey: "expiration",
      accessorFn: (row: CertificateInstance) => row.expiration,
      header: "Expires",
      cell: (info) => {
        const { text, className } = getExpirationStyle(info.getValue() as string);
        return <span className={className}>{text}</span>;
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      accessorFn: (row: CertificateInstance) => row.created_at,
      header: "Created At",
      cell: (info) => (
        <span className="text-gray-600 text-sm">
          {moment.tz(info.getValue() as string, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")}
        </span>
      ),
    },
    {
      id: "created_by",
      accessorKey: "created_by.name",
      header: "Created By",
    },
    {
      id: "birthday_fund_request",
      accessorKey: "birthday_fund_request_id",
      header: "Actions",
      disableRowClick: true,
      cell: (info) => {
        const requestId = info.getValue() as string | undefined;
        
        return (
          <div className="flex flex-row gap-4">
          <Button
            variant="outline"
            size="sm"
            disabled={!requestId}
            onClick={(e) => {
              e.stopPropagation();
              if (requestId) {
                setSearchParams(prev => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set("request_id", requestId);
                  return newParams;
                });
              }
            }}
          >
            View Request
          </Button>
        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(info.row.original);
          }}
        >
          Delete
        </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <ViewBirthdayFundRequest />
      {/* Preset Filter Buttons */}
      <div className="flex items-center gap-2 mx-4">
        <span className="text-sm font-medium text-muted-foreground mr-2">Show:</span>
        <Button
          variant={filters.not_expired ? "default" : "outline"}
          size="sm"
          onClick={() => toggleFilter("not_expired")}
          className={filters.not_expired ? "bg-green-600 hover:bg-green-700" : ""}
        >
          Not Expired
        </Button>
        
        <Button
          variant={filters.expired ? "default" : "outline"}
          size="sm"
          onClick={() => toggleFilter("expired")}
          className={filters.expired ? "bg-red-600 hover:bg-red-700" : ""}
        >
          Expired
        </Button>
        
        <Button
          variant={filters.about_to_expire ? "default" : "outline"}
          size="sm"
          onClick={() => toggleFilter("about_to_expire")}
          className={filters.about_to_expire ? "bg-yellow-600 hover:bg-yellow-700" : ""}
        >
          About to Expire
        </Button>
      </div>

      <DataTable
        defaultSorting={[{ id: "created_at", desc: true }]}
        tableClassName="max-h-[680px] overflow-y-scroll"
        columns={columns}
        data={filteredInstances}
        className="mx-4"
        enableSorting
        enableMultiSort
        enableFiltering
        enableColumnVisibility
        enableGrouping
        enableExport
        exportFilename={() => ["certificate_list", moment().format("YYYYMMDD")].join("_")}
        stickyHeaders
        filterStyle="select"
        filterMode="internal"
        emptyMessage="No certificates found"
        onRowClick={handleRowClick}
        rowClickable
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Certificate</DialogTitle>
          </DialogHeader>
          
          {selectedCertificate && (
            <div className="space-y-4">
              {/* Certificate Details (Read-only) */}
              <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                <h4 className="font-semibold text-sm text-muted-foreground">Certificate Details</h4>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Certificate ID:</span>
                  <span className="font-mono">{selectedCertificate.certificate_id}</span>
                  
                  <span className="text-muted-foreground">Location:</span>
                  <span>{selectedCertificate.location_name}</span>
                  
                  <span className="text-muted-foreground">Created:</span>
                  <span>{moment.tz(selectedCertificate.created_at, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")}</span>
                  
                  <span className="text-muted-foreground">Expires:</span>
                  <span className={getExpirationStyle(selectedCertificate.expiration).className}>
                    {getExpirationStyle(selectedCertificate.expiration).text}
                  </span>
                </div>
              </div>

              {/* Status Selection */}
              <div className="space-y-2 grid">
                <label className="text-sm font-medium">Status</label>
                <Combobox
                  isModal
                  options={statusOptions}
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                  placeholder="Select status"
                  dwimOnChange
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdateStatus}
                  disabled={isUpdating || selectedStatus === selectedCertificate.status}
                >
                  {isUpdating ? "Updating..." : "Update Status"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Certificate</DialogTitle>
          </DialogHeader>
          
          {certificateToDelete && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete this certificate? This action cannot be undone.
              </p>
              
              <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Certificate ID:</span>
                  <span className="font-mono">{certificateToDelete.certificate_id}</span>
                  
                  <span className="text-muted-foreground">Location:</span>
                  <span>{certificateToDelete.location_name}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
